import { useState, useEffect } from 'react';
import { bitable } from '@lark-base-open/js-sdk';
import { Button, Toast, Typography, Spin, Select, Switch, Progress, Banner, Divider } from '@douyinfe/semi-ui';
import { IconUpload, IconFile } from '@douyinfe/semi-icons';
import { useBaseData, AttachmentInfo } from './hooks/useBaseData';
import { uploadToGoogleDrive } from './services/api';
import './styles/index.css';

const { Title, Text } = Typography;

function App() {
    const {
        appToken,
        tableId,
        fields,
        attachmentFields,
        loading,
        error,
        getAttachments,
        getFieldValue,
        updateFieldValue,
        getAllRecords
    } = useBaseData();

    const [currentRecordId, setCurrentRecordId] = useState<string>('');
    const [selectedFieldId, setSelectedFieldId] = useState<string>('');
    const [selectedRenameFieldId, setSelectedRenameFieldId] = useState<string>('');
    const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);
    const [selectedAttachment, setSelectedAttachment] = useState<AttachmentInfo | null>(null);
    const [uploading, setUploading] = useState(false);

    // Batch Upload State
    const [isBatchMode, setIsBatchMode] = useState(false);
    const [statusFieldId, setStatusFieldId] = useState<string>('');
    const [skipProcessed, setSkipProcessed] = useState(true);
    const [batchProgress, setBatchProgress] = useState(0);
    const [batchTotal, setBatchTotal] = useState(0);
    const [batchProcessing, setBatchProcessing] = useState(false);
    const [batchLogs, setBatchLogs] = useState<string[]>([]);

    // 监听当前选中的记录
    useEffect(() => {
        const unsubscribe = bitable.base.onSelectionChange(async (event) => {
            if (event.data.recordId) {
                setCurrentRecordId(event.data.recordId);
                // 清空之前的选择
                if (!isBatchMode) {
                    setSelectedAttachment(null);
                    setAttachments([]);
                }
            }
        });

        // 初始化时获取当前选中的记录
        bitable.base.getSelection().then((selection) => {
            if (selection?.recordId) {
                setCurrentRecordId(selection.recordId);
            }
        });

        return () => {
            unsubscribe();
        };
    }, [isBatchMode]);

    // 当选择了附件字段时，加载附件 (仅在非批量模式下)
    useEffect(() => {
        if (currentRecordId && selectedFieldId && !isBatchMode) {
            loadAttachments();
        }
    }, [currentRecordId, selectedFieldId, isBatchMode]);

    const loadAttachments = async () => {
        if (!currentRecordId || !selectedFieldId) return;

        const atts = await getAttachments(currentRecordId, selectedFieldId);
        setAttachments(atts);
        if (atts.length > 0) {
            setSelectedAttachment(atts[0]);
        }
    };

    const handleUpload = async () => {
        if (!selectedAttachment || !currentRecordId) {
            Toast.warning('请先选择要上传的附件');
            return;
        }
        if (!statusFieldId) {
            Toast.warning('请选择状态回写字段');
            return;
        }

        if (!appToken) {
            Toast.error('无法获取应用令牌');
            return;
        }

        setUploading(true);

        try {
            // Find the name of the rename field
            const renameField = fields.find(f => f.id === selectedRenameFieldId);
            const renameFieldName = renameField ? renameField.name : undefined;

            const result = await uploadToGoogleDrive({
                fileToken: selectedAttachment.fileToken,
                fileName: selectedAttachment.name,
                tableId,
                recordId: currentRecordId,
                renameFieldId: selectedRenameFieldId || undefined,
                renameFieldName: renameFieldName,
                appToken
            });

            if (result.success) {
                const writeSuccess = await updateFieldValue(currentRecordId, statusFieldId, '上传成功');

                if (writeSuccess) {
                    Toast.success('上传成功');
                } else {
                    Toast.warning('上传成功但回写失败');
                }
            } else {
                Toast.error(`上传失败: ${result.error}`);
                await updateFieldValue(currentRecordId, statusFieldId, `上传失败: ${result.error}`);
            }
        } catch (err: any) {
            console.error('Single upload exception:', err);
            Toast.error(`上传异常: ${err.message}`);
            await updateFieldValue(currentRecordId, statusFieldId, `上传异常: ${err.message}`);
        } finally {
            setUploading(false);
        }
    };

    const handleBatchUpload = async () => {
        if (!statusFieldId) {
            Toast.warning('请选择状态回写字段');
            return;
        }

        if (!appToken) {
            Toast.error('无法获取应用令牌');
            return;
        }

        if (!selectedFieldId) {
            Toast.warning('请选择附件字段');
            return;
        }

        setBatchProcessing(true);
        setBatchLogs([]);
        setBatchProgress(0);

        const renameField = fields.find(f => f.id === selectedRenameFieldId);
        const renameFieldName = renameField ? renameField.name : undefined;

        setTimeout(async () => {
            try {
                const recordIds = await getAllRecords();
                setBatchTotal(recordIds.length);

                let successCount = 0;
                let failCount = 0;
                let skipCount = 0;

                for (let i = 0; i < recordIds.length; i++) {
                    const recordId = recordIds[i];

                    if (skipProcessed) {
                        const currentStatus = await getFieldValue(recordId, statusFieldId);
                        if (currentStatus && (currentStatus === '上传成功' || currentStatus.includes('Successful'))) {
                            setBatchLogs(prev => [`[${i + 1}/${recordIds.length}] 跳过: ${recordId}`, ...prev]);
                            skipCount++;
                            setBatchProgress(i + 1);
                            continue;
                        }
                    }

                    try {
                        const recordAttachments = await getAttachments(recordId, selectedFieldId);

                        if (recordAttachments.length === 0) {
                            setBatchLogs(prev => [`[${i + 1}/${recordIds.length}] 无附件: ${recordId}`, ...prev]);
                            setBatchProgress(i + 1);
                            continue;
                        }

                        let recordHasError = false;
                        let recordErrorMsg = '';

                        for (const att of recordAttachments) {
                            setBatchLogs(prev => [`[${i + 1}/${recordIds.length}] 上传中: ${att.name}...`, ...prev]);

                            try {
                                const result = await uploadToGoogleDrive({
                                    fileToken: att.fileToken,
                                    fileName: att.name,
                                    tableId,
                                    recordId,
                                    renameFieldId: selectedRenameFieldId || undefined,
                                    renameFieldName: renameFieldName,
                                    appToken
                                });

                                if (result.success) {
                                    setBatchLogs(prev => [`[${i + 1}/${recordIds.length}] 成功: ${att.name}`, ...prev]);
                                    successCount++;
                                } else {
                                    recordHasError = true;
                                    recordErrorMsg = result.error || 'Unknown error';
                                    setBatchLogs(prev => [`[${i + 1}/${recordIds.length}] 失败: ${att.name} - ${result.error}`, ...prev]);
                                    failCount++;
                                }
                            } catch (err: any) {
                                recordHasError = true;
                                recordErrorMsg = err.message;
                                setBatchLogs(prev => [`[${i + 1}/${recordIds.length}] 异常: ${att.name} - ${err.message}`, ...prev]);
                                failCount++;
                            }
                        }

                        if (recordHasError) {
                            await updateFieldValue(recordId, statusFieldId, `上传失败: ${recordErrorMsg}`);
                        } else {
                            await updateFieldValue(recordId, statusFieldId, '上传成功');
                        }

                    } catch (err: any) {
                        setBatchLogs(prev => [`[${i + 1}/${recordIds.length}] 记录处理错误: ${err.message}`, ...prev]);
                        failCount++;
                    }

                    setBatchProgress(i + 1);
                    await new Promise(resolve => setTimeout(resolve, 200));
                }

                Toast.info(`完成: 成功 ${successCount}, 失败 ${failCount}, 跳过 ${skipCount}`);

            } catch (err: any) {
                Toast.error(`批量处理异常: ${err.message}`);
            } finally {
                setBatchProcessing(false);
            }
        }, 100);
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
                <Spin size="large" />
                <Text style={{ marginTop: 16 }}>加载中...</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 20, textAlign: 'center' }}>
                <Text type="danger">错误: {error}</Text>
                <Button onClick={() => window.location.reload()} style={{ marginTop: 16 }}>重试</Button>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: 'var(--semi-color-bg-0)' }}>
            {/* Header */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--semi-color-border)', flexShrink: 0, background: 'var(--semi-color-bg-0)', zIndex: 10 }}>
                <Title heading={4} style={{ marginBottom: '12px', textAlign: 'center' }}>Feishu Drive Sync</Title>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div style={{ background: 'var(--semi-color-fill-0)', padding: '4px', borderRadius: '8px', display: 'flex', width: '100%' }}>
                        <Button
                            theme={!isBatchMode ? 'solid' : 'light'}
                            type={!isBatchMode ? 'primary' : 'tertiary'}
                            onClick={() => setIsBatchMode(false)}
                            style={{ borderRadius: '6px', flex: 1 }}
                        >
                            单文件
                        </Button>
                        <Button
                            theme={isBatchMode ? 'solid' : 'light'}
                            type={isBatchMode ? 'primary' : 'tertiary'}
                            onClick={() => setIsBatchMode(true)}
                            style={{ borderRadius: '6px', flex: 1 }}
                        >
                            批量
                        </Button>
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                    {/* Basic Settings Section - Flat Design */}
                    <div style={{ width: '100%' }}>
                        <Text strong style={{ marginBottom: '12px', display: 'block', fontSize: '14px' }}>基础设置</Text>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ width: '100%' }}>
                                <Text type="secondary" size="small" style={{ marginBottom: '4px', display: 'block' }}>附件字段 (必选)</Text>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="选择附件列"
                                    value={selectedFieldId}
                                    onChange={(value) => setSelectedFieldId(value as string)}
                                    disabled={batchProcessing}
                                >
                                    {attachmentFields.map(field => (
                                        <Select.Option key={field.id} value={field.id}>{field.name}</Select.Option>
                                    ))}
                                </Select>
                            </div>

                            <div style={{ width: '100%' }}>
                                <Text type="secondary" size="small" style={{ marginBottom: '4px', display: 'block' }}>重命名依据 (可选)</Text>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="不重命名"
                                    value={selectedRenameFieldId}
                                    onChange={(value) => setSelectedRenameFieldId(value as string)}
                                    disabled={uploading || batchProcessing}
                                    showClear
                                >
                                    {fields.map(field => (
                                        <Select.Option key={field.id} value={field.id}>{field.name}</Select.Option>
                                    ))}
                                </Select>
                            </div>

                            <div style={{ width: '100%' }}>
                                <Text type="secondary" size="small" style={{ marginBottom: '4px', display: 'block' }}>状态回写 (必选)</Text>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="选择文本列"
                                    value={statusFieldId}
                                    onChange={(value) => setStatusFieldId(value as string)}
                                    disabled={uploading || batchProcessing}
                                >
                                    {fields
                                        .filter(f => f.type === 1 || f.type === 15)
                                        .map(field => (
                                            <Select.Option key={field.id} value={field.id}>{field.name}</Select.Option>
                                        ))}
                                </Select>
                            </div>
                        </div>
                    </div>

                    <Divider />

                    {/* Mode Specific Options */}
                    {isBatchMode ? (
                        <div style={{ width: '100%' }}>
                            <Text strong style={{ marginBottom: '12px', display: 'block', fontSize: '14px' }}>批量选项</Text>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Text>跳过已上传</Text>
                                    <Switch checked={skipProcessed} onChange={setSkipProcessed} disabled={batchProcessing} />
                                </div>
                                <Banner
                                    type="info"
                                    description="将处理当前视图所有记录。请先筛选。"
                                    closeIcon={null}
                                    style={{ borderRadius: '4px' }}
                                />

                                {batchProcessing && (
                                    <div style={{ marginTop: '8px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <Text type="secondary" size="small">进度: {batchProgress}/{batchTotal}</Text>
                                        </div>
                                        <Progress
                                            percent={batchTotal > 0 ? Math.round((batchProgress / batchTotal) * 100) : 0}
                                            stroke="var(--semi-color-primary)"
                                            style={{ height: '6px' }}
                                        />
                                    </div>
                                )}

                                {batchLogs.length > 0 && (
                                    <div style={{
                                        background: 'var(--semi-color-fill-0)',
                                        padding: '8px',
                                        borderRadius: '4px',
                                        maxHeight: '120px',
                                        overflowY: 'auto',
                                        fontSize: '12px',
                                        border: '1px solid var(--semi-color-border)'
                                    }}>
                                        {batchLogs.map((log, index) => (
                                            <div key={index} style={{ marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{log}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div style={{ width: '100%' }}>
                            <Text strong style={{ marginBottom: '12px', display: 'block', fontSize: '14px' }}>单文件选项</Text>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {!currentRecordId ? (
                                    <Banner type="warning" description="请先点击表格中的一行" closeIcon={null} />
                                ) : (
                                    <div style={{ width: '100%' }}>
                                        <Text type="secondary" size="small" style={{ marginBottom: '4px', display: 'block' }}>选择附件</Text>
                                        <Select
                                            style={{ width: '100%' }}
                                            placeholder="选择文件"
                                            value={selectedAttachment?.fileToken}
                                            onChange={(value) => {
                                                const att = attachments.find(a => a.fileToken === value);
                                                setSelectedAttachment(att || null);
                                            }}
                                            disabled={uploading || !currentRecordId}
                                        >
                                            {attachments.map(att => (
                                                <Select.Option key={att.fileToken} value={att.fileToken}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <IconFile />
                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</span>
                                                    </div>
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </div>
                                )}

                                {uploading && (
                                    <div style={{ marginTop: '8px' }}>
                                        <Text type="secondary" size="small">上传中...</Text>
                                        <Progress percent={60} showInfo={false} style={{ height: '4px', marginTop: '4px' }} />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer - Fixed Action Button */}
            <div style={{ padding: '16px', borderTop: '1px solid var(--semi-color-border)', flexShrink: 0, background: 'var(--semi-color-bg-0)', zIndex: 10 }}>
                {isBatchMode ? (
                    <Button
                        theme="solid"
                        type="primary"
                        block
                        size="large"
                        onClick={handleBatchUpload}
                        loading={batchProcessing}
                        disabled={!statusFieldId || !selectedFieldId || batchProcessing}
                        style={{ fontWeight: 600 }}
                    >
                        {batchProcessing ? '停止/处理中' : '开始批量上传'}
                    </Button>
                ) : (
                    <Button
                        theme="solid"
                        type="primary"
                        block
                        size="large"
                        icon={<IconUpload />}
                        onClick={handleUpload}
                        loading={uploading}
                        disabled={!selectedAttachment || !statusFieldId || uploading}
                        style={{ fontWeight: 600 }}
                    >
                        {uploading ? '上传中...' : '上传到 Google Drive'}
                    </Button>
                )}
            </div>
        </div>
    );
}

export default App;
